import { Fragment, type CSSProperties, type ReactNode } from 'react'
import type { Doc, DocMark, DocNode, DocText } from '@publiqa/shared'
import { embedSrc } from '@publiqa/shared'
import { safeHref } from '../lib/richtext'
import { usePublicHref } from './context'
import { Gallery } from './Gallery'

/**
 * Rendu du corps d'article (document de l'éditeur WYSIWYG).
 *
 * ⭐ Aucun HTML injecté : chaque nœud connu devient un élément React (qui
 * échappe le texte), les nœuds inconnus sont ignorés, les liens repassent par
 * `safeHref` et une vidéo n'est intégrée que depuis YouTube, Vimeo ou
 * Dailymotion — même si la base contenait autre chose.
 */
export function DocView({ doc }: { doc: Doc }) {
  return <>{renderNodes(doc.content)}</>
}

function renderNodes(nodes: (DocNode | DocText)[] | undefined): ReactNode {
  return (nodes ?? []).map((n, i) => <Node key={i} node={n} />)
}

const ALIGNS = new Set(['left', 'center', 'right', 'justify'])
function alignStyle(node: DocNode): CSSProperties | undefined {
  const a = node.attrs?.textAlign
  return a && a !== 'left' && ALIGNS.has(a) ? { textAlign: a } : undefined
}

function Node({ node }: { node: DocNode | DocText }) {
  if (node.type === 'text') return <Text node={node} />
  switch (node.type) {
    case 'paragraph':
      return <p style={alignStyle(node)}>{renderNodes(node.content)}</p>
    case 'heading':
      return node.attrs?.level === 3 ? (
        <h3 style={alignStyle(node)}>{renderNodes(node.content)}</h3>
      ) : (
        <h2 style={alignStyle(node)}>{renderNodes(node.content)}</h2>
      )
    case 'blockquote':
      return <blockquote>{renderNodes(node.content)}</blockquote>
    case 'bulletList':
      return <ul>{renderNodes(node.content)}</ul>
    case 'orderedList':
      return (
        <ol start={node.attrs?.start && node.attrs.start !== 1 ? node.attrs.start : undefined}>
          {renderNodes(node.content)}
        </ol>
      )
    case 'listItem':
      return <li>{renderNodes(node.content)}</li>
    case 'horizontalRule':
      return <hr />
    case 'hardBreak':
      return <br />
    case 'figure': {
      const a = node.attrs ?? {}
      if (!a.src || !/^(\/uploads\/|\/templates\/|https:\/\/)/.test(a.src)) return null
      return (
        <figure className={`doc-figure size-${a.size ?? 'normal'}`}>
          <img src={a.src} alt={a.alt || a.caption || ''} loading="lazy" decoding="async" />
          {a.caption && <figcaption>{a.caption}</figcaption>}
        </figure>
      )
    }
    case 'gallery':
      return <Gallery images={node.attrs?.images ?? []} caption={node.attrs?.caption ?? ''} />
    case 'embed': {
      const src = embedSrc(node.attrs?.url ?? '')
      if (!src) return null
      return (
        <figure className="doc-embed">
          <div className="doc-embed-frame">
            <iframe
              src={src}
              title={node.attrs?.caption || 'Vidéo'}
              loading="lazy"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
          {node.attrs?.caption && <figcaption>{node.attrs.caption}</figcaption>}
        </figure>
      )
    }
    case 'callout':
      return (
        <aside className="doc-callout">
          {node.attrs?.title && <p className="doc-callout-title">{node.attrs.title}</p>}
          {renderNodes(node.content)}
        </aside>
      )
    default:
      return null
  }
}

function Text({ node }: { node: DocText }) {
  const href = usePublicHref()
  let out: ReactNode = node.text
  // Ordre stable : le lien enveloppe le reste, comme dans l'éditeur.
  const marks = [...(node.marks ?? [])].sort((a, b) => order(a) - order(b))
  for (const m of marks) {
    switch (m.type) {
      case 'bold':
        out = <strong>{out}</strong>
        break
      case 'italic':
        out = <em>{out}</em>
        break
      case 'underline':
        out = <u>{out}</u>
        break
      case 'strike':
        out = <s>{out}</s>
        break
      case 'link': {
        const url = href(safeHref(m.attrs.href))
        const external = /^https?:/.test(url)
        out = (
          <a href={url} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {out}
          </a>
        )
        break
      }
    }
  }
  return <Fragment>{out}</Fragment>
}

const order = (m: DocMark) => (m.type === 'link' ? 1 : 0)
